import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { LogOut } from "lucide-react";

// Each tab is gated by a single capability. Admin always has all of them via defaults.
const TABS = [
  { to: "/dashboard", label: "Dashboard", cap: "view_dashboard" },
  { to: "/heatmap", label: "Heat Map", cap: "view_heatmap" },
  { to: "/streams", label: "Streams", cap: "view_streams" },
  { to: "/employees", label: "Employees", cap: "view_employees" },
  { to: "/projects", label: "Projects", cap: "view_projects" },
  { to: "/revenue", label: "Revenue", cap: "view_revenue" },
  { to: "/leaves", label: "Leave Dashboard", cap: "view_leaves" },
  // Upload tab visible if user can upload at least one kind
  { to: "/upload", label: "Upload", capAny: ["upload_billability", "upload_leaves"] },
  { to: "/history", label: "History", cap: "view_history" },
  { to: "/users", label: "Users", cap: "manage_users" },
  { to: "/entitlements", label: "Entitlements", cap: "manage_entitlements" },
  { to: "/settings", label: "Settings" }, // always visible — admins can edit, others read-only
];

export default function Shell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const caps = new Set(user?.capabilities || []);

  const visibleTabs = TABS.filter((t) => {
    if (t.cap) return caps.has(t.cap);
    if (t.capAny) return t.capAny.some((c) => caps.has(c));
    return true;
  });

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <header className="h-14 flex items-center px-6 gap-3 sticky top-0 z-50 shadow-md"
              style={{ background: "#1a3666" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-black text-base"
               style={{ background: "#00a3a3" }}>v</div>
          <span className="text-white font-bold text-base">valantic</span>
          <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                style={{ background: "rgba(255,255,255,.15)", color: "rgba(255,255,255,.9)" }}>vSDI</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-2.5 text-white/90 text-sm">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
               style={{ background: "#00a3a3" }}>
            {(user?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
          </div>
          <div className="hidden sm:block">
            <div data-testid="topbar-user-name">{user?.name}</div>
            <div className="text-[11px] text-white/60 uppercase">{user?.role}</div>
          </div>
          <button data-testid="btn-logout" onClick={async () => { await logout(); nav("/"); }}
                  className="ml-2 px-3 py-1.5 rounded-md text-sm bg-white/15 hover:bg-white/25 flex items-center gap-1.5">
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      </header>

      <nav className="bg-white border-b border-slate-200 px-6 flex gap-1 overflow-x-auto">
        {visibleTabs.map((t) => (
          <NavLink key={t.to} to={t.to} data-testid={`tab-${t.to.slice(1)}`}
                   className={({ isActive }) =>
                     `px-4 py-3 text-sm font-medium whitespace-nowrap border-b-[2.5px] transition ${
                       isActive ? "border-teal-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
                     }`
                   }>
            {t.label}
          </NavLink>
        ))}
      </nav>

      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
